// Playback for text-to-speech audio. Plays an audio blob (MP3 from the server)
// and hands back a handle so the caller can stop it when leaving the block.
//
// Autoplay note: browsers may block playback that isn't tied to a user gesture.
// The participant's Send click usually unlocks audio for the session, so the
// reply that follows plays fine. If a play is blocked anyway, `done` still
// resolves (we don't hang) and the caller keeps the text on screen.

export interface Playback {
  stop: () => void;
  done: Promise<void>;
}

export function playAudioBlob(blob: Blob): Playback {
  const url = URL.createObjectURL(blob);
  const audio = new Audio(url);
  let settled = false;

  const done = new Promise<void>((resolve) => {
    const finish = () => {
      if (settled) return;
      settled = true;
      URL.revokeObjectURL(url);
      resolve();
    };
    audio.onended = finish;
    audio.onerror = finish;
    void Promise.resolve(audio.play()).catch(() => {
      // Autoplay blocked or playback failed: don't hang the flow.
      finish();
    });
  });

  return {
    stop: () => {
      try {
        audio.pause();
      } catch {
        // ignore
      }
      if (!settled) {
        settled = true;
        URL.revokeObjectURL(url);
      }
    },
    done,
  };
}
